import { apiErrorField } from '@agos/api-client'
import {
  getGetRecurringInvoicesQueryKey,
  useDeleteRecurringInvoicesTemplateId,
  useGetRecurringInvoices,
  usePatchRecurringInvoicesTemplateId,
  usePostRecurringInvoices,
  usePostRecurringInvoicesRun,
} from '@agos/api-client/invoicing'
import { useGetSuppliers } from '@agos/api-client/inventory'
import { T, formatCents, formatIban, parseMoneyToCents, tt } from '@agos/shared'
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  cn,
  type Column,
  ConfirmDialog,
  DataTable,
  FilterBar,
  IconButton,
  PageHeader,
  Spinner,
  StatCard,
  StatusBadge,
  useToast,
} from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import {
  AlertCircle,
  CalendarClock,
  Edit2,
  Info,
  PlayCircle,
  Plus,
  Repeat,
  Trash2,
  X,
} from 'lucide-react'
import { useMemo, useState, type ReactNode } from 'react'

import {
  MAX_DAY_OF_MONTH,
  byUrgency,
  canSave,
  formOf,
  isDueNow,
  matchesSearch,
  nextOccurrence,
  readRun,
  runMessage,
  patchPayload,
  templatePayload,
  templateProblems,
  templateTotals,
  type RecurringTemplate,
  type RunOutcome,
  type TemplateForm,
  type TemplateProblem,
} from './recurring-template'

/**
 * `/recurring-invoices` — the standing charges that bill themselves.
 *
 * **FB-085.** `next_due_date` is a column the generator writes, so a template that has
 * never run has none — and three of the four in the restored data are exactly that.
 * Legacy's *Ďalšia splatnosť* column is therefore empty for three live standing charges,
 * and nothing says that **all three are due the moment somebody presses Spustiť**.
 *
 * Both are computable from what the page already has: the backend's own three conditions
 * (active, day of month reached, not generated this calendar month) and the day of month
 * itself. So they are computed, marked as derived, and the run is confirmed with the
 * count.
 *
 * Ported from legacy `pages/recurring-invoices.tsx`.
 */

interface SupplierOption {
  id?: number
  name?: string
}

export function RecurringInvoicesPage() {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  // Read once for the visit: what is "due now" does not change while somebody looks at
  // the list, and a value that moved every render could not be memoised on.
  const [now] = useState(() => Date.now())

  const [search, setSearch] = useState('')
  const [editing, setEditing] = useState<{ template: RecurringTemplate | null } | null>(null)
  const [deleting, setDeleting] = useState<RecurringTemplate | null>(null)
  const [confirmingRun, setConfirmingRun] = useState(false)
  const [outcome, setOutcome] = useState<RunOutcome | null>(null)

  const { data, isLoading } = useGetRecurringInvoices()
  const templates = useMemo<RecurringTemplate[]>(() => data ?? [], [data])
  const shown = useMemo(
    () => byUrgency(templates.filter((template) => matchesSearch(template, search)), now),
    [templates, search, now],
  )
  const totals = templateTotals(templates, now)

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: getGetRecurringInvoicesQueryKey() })
  }
  const onError = (error: unknown) =>
    toast({
      title: tt('Chyba'),
      description: messageOf(error) ?? tt('Operácia sa nepodarila.'),
      variant: 'destructive',
    })

  const remove = useDeleteRecurringInvoicesTemplateId({
    mutation: {
      onSuccess: () => {
        setDeleting(null)
        invalidate()
      },
      onError,
    },
  })

  const run = usePostRecurringInvoicesRun({
    mutation: {
      onSuccess: (result: unknown) => {
        setOutcome(readRun(result))
        invalidate()
      },
      onError,
    },
  })

  const columns: Column<RecurringTemplate>[] = useMemo(
    () => [
      {
        key: 'name',
        header: tt('Šablóna'),
        sortable: true,
        sortValue: (template) => template.name ?? '',
        render: (template) => (
          <div className="min-w-0">
            <p className="font-semibold text-sm truncate flex items-center gap-1.5">
              {template.name}
              {isDueNow(template, now) && (
                <StatusBadge color="amber" label={tt('splatná teraz')} icon={AlertCircle} />
              )}
            </p>
            <p className="text-[11px] text-muted-foreground truncate">{template.supplierName}</p>
          </div>
        ),
      },
      {
        key: 'amountGross',
        header: tt('Suma'),
        sortable: true,
        sortValue: (template) => parseMoneyToCents(template.amountGross),
        className: 'text-right',
        headerClassName: 'text-right',
        render: (template) => (
          <span className="font-bold tabular-nums whitespace-nowrap">
            {formatCents(parseMoneyToCents(template.amountGross))}
            {(template.vatRate ?? 0) > 0 && (
              <span className="text-[11px] text-muted-foreground font-normal">
                {' '}
                · {template.vatRate} %
              </span>
            )}
          </span>
        ),
      },
      {
        key: 'dayOfMonth',
        header: tt('Deň v mesiaci'),
        sortable: true,
        sortValue: (template) => template.dayOfMonth ?? 0,
        className: 'text-center',
        headerClassName: 'text-center',
        render: (template) => <span className="text-sm tabular-nums">{template.dayOfMonth}.</span>,
      },
      {
        // FB-085 — the server writes this only once a template has generated.
        key: 'next',
        header: tt('Ďalšia splatnosť'),
        sortable: true,
        sortValue: (template) => nextOccurrence(template, now)?.issueDate ?? '',
        render: (template) => {
          const next = nextOccurrence(template, now)
          if (!next) return <span className="text-xs text-muted-foreground">-</span>
          return (
            <span className="text-xs whitespace-nowrap">
              {next.issueDate}
              <span className="text-muted-foreground">
                {' '}
                → {next.dueDate}
                {next.derived && ` · ${tt('odvodené')}`}
              </span>
            </span>
          )
        },
      },
      {
        key: 'active',
        header: tt('Stav'),
        sortable: true,
        sortValue: (template) => (template.active === false ? 1 : 0),
        render: (template) =>
          template.active === false ? (
            <StatusBadge color="zinc" label={tt('Pozastavená')} />
          ) : (
            <StatusBadge color="green" label={tt('Aktívna')} />
          ),
      },
      {
        key: 'actions',
        header: '',
        render: (template) => (
          <div className="flex items-center gap-1 justify-end">
            <IconButton
              label={`${tt('Upraviť')} - ${template.name ?? ''}`}
              onClick={() => setEditing({ template })}
            >
              <Edit2 className="w-3.5 h-3.5" />
            </IconButton>
            <IconButton
              label={`${tt('Zmazať')} - ${template.name ?? ''}`}
              tone="red"
              onClick={() => setDeleting(template)}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </IconButton>
          </div>
        ),
      },
    ],
    [now],
  )

  return (
    <div className="space-y-5">
      <PageHeader
        title={tt('Opakované faktúry')}
        description={tt('Šablóny, z ktorých sa každý mesiac vytvorí faktúra')}
        icon={<Repeat className="w-5 h-5" />}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setConfirmingRun(true)}
              disabled={run.isPending}
              className="flex items-center gap-2 px-4 py-2 rounded-xl border border-border text-sm font-semibold hover:bg-muted/50 transition-colors disabled:opacity-50"
            >
              {run.isPending ? <Spinner className="w-4 h-4" /> : <PlayCircle className="w-4 h-4" />}
              <T>Spustiť generovanie</T>
            </button>
            <button
              type="button"
              onClick={() => setEditing({ template: null })}
              className={cn('flex items-center gap-2 px-4 py-2 rounded-xl', BUTTON_PRIMARY, 'text-sm')}
            >
              <Plus className="w-4 h-4" />
              <T>Nová šablóna</T>
            </button>
          </div>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-template-totals>
        <StatCard
          label={tt('Šablón')}
          value={String(totals.count)}
          icon={Repeat}
          color="blue"
          sub={totals.activeCount < totals.count ? `${totals.activeCount} ${tt('aktívnych')}` : undefined}
        />
        <StatCard
          label={tt('Mesačne')}
          value={formatCents(totals.monthlyCents)}
          icon={CalendarClock}
          color="violet"
        />
        <StatCard
          label={tt('Splatných teraz')}
          value={String(totals.dueNowCount)}
          icon={AlertCircle}
          color={totals.dueNowCount > 0 ? 'amber' : 'emerald'}
        />
        <StatCard
          label={tt('Nikdy nespustených')}
          value={String(totals.neverRunCount)}
          icon={Info}
          color={totals.neverRunCount > 0 ? 'amber' : 'emerald'}
        />
      </div>

      {/* FB-085 - pressing the button bills these suppliers, and legacy says nothing. */}
      {totals.dueNowCount > 0 && (
        <p className="flex items-start gap-1.5 text-xs text-amber-500" data-due-now-warning>
          <AlertCircle className="w-4 h-4 shrink-0 mt-px" />
          <span>
            <strong>{totals.dueNowCount}</strong>{' '}
            <T>
              šablón je splatných - spustenie generovania z nich hneď vytvorí faktúry. Deň v mesiaci
              už prešiel a tento mesiac ešte negenerovali.
            </T>
          </span>
        </p>
      )}

      {totals.neverRunCount > 0 && (
        <p className="flex items-start gap-1.5 text-[11px] text-muted-foreground" data-derived-note>
          <Info className="w-3.5 h-3.5 shrink-0 mt-px" />
          <T>
            Šablóna, ktorá ešte nikdy negenerovala, nemá od servera dátum ďalšej splatnosti - je
            odvodený z dňa v mesiaci.
          </T>
        </p>
      )}

      <FilterBar
        search={{
          value: search,
          onChange: setSearch,
          placeholder: tt('Hľadať šablónu, dodávateľa, IBAN...'),
        }}
      />

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Spinner />
        </div>
      ) : (
        <div data-templates>
          <DataTable<RecurringTemplate>
            columns={columns}
            data={shown}
            rowKey={(template) => template.id ?? 0}
            pageSize={25}
            compact
            emptyMessage={tt('Žiadne šablóny')}
            emptyIcon={<Repeat className="w-10 h-10 mb-3 opacity-30" />}
          />
        </div>
      )}

      {outcome && (
        <div
          className="rounded-xl border border-border bg-muted/40 p-3 text-xs space-y-1"
          data-run-outcome
        >
          <p className="font-semibold">{runMessage(outcome)}</p>
          <p className="text-muted-foreground tabular-nums">
            <T>Splatných</T> {outcome.scanned} · <T>vygenerovaných</T> {outcome.generated}
          </p>
        </div>
      )}

      {editing && (
        <TemplateEditor template={editing.template} onClose={() => setEditing(null)} />
      )}

      <ConfirmDialog
        open={confirmingRun}
        onClose={() => setConfirmingRun(false)}
        onConfirm={() => {
          setConfirmingRun(false)
          setOutcome(null)
          run.mutate()
        }}
        title={tt('Vygenerovať faktúry zo šablón?')}
        description={
          totals.dueNowCount > 0
            ? `${tt('Splatných šablón:')} ${totals.dueNowCount}. ${tt('Z každej sa vytvorí faktúra, ktorú bude treba zaplatiť.')}`
            : tt('Žiadna šablóna nie je splatná, takže sa nič nevytvorí.')
        }
        confirmLabel={tt('Vygenerovať')}
        variant={totals.dueNowCount > 0 ? 'danger' : 'default'}
      />

      <ConfirmDialog
        open={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={() => deleting?.id !== undefined && remove.mutate({ templateId: deleting.id })}
        title={`${tt('Zmazať šablónu')} „${deleting?.name ?? ''}"?`}
        description={tt(
          'Faktúry, ktoré z nej už vznikli, zostanú. Prestane len vytvárať nové - na to stačí aj pozastaviť ju.',
        )}
        confirmLabel={tt('Zmazať')}
        variant="danger"
        loading={remove.isPending}
      />
    </div>
  )
}

function TemplateEditor({
  template,
  onClose,
}: {
  template: RecurringTemplate | null
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const [form, setForm] = useState<TemplateForm>(() => formOf(template))

  const { data: supplierData } = useGetSuppliers()
  const suppliers = useMemo<SupplierOption[]>(() => supplierData ?? [], [supplierData])

  const done = () => {
    void queryClient.invalidateQueries({ queryKey: getGetRecurringInvoicesQueryKey() })
    onClose()
  }
  const onError = (error: unknown) =>
    toast({
      title: tt('Chyba'),
      description: messageOf(error) ?? tt('Šablónu sa nepodarilo uložiť.'),
      variant: 'destructive',
    })

  const create = usePostRecurringInvoices({ mutation: { onSuccess: done, onError } })
  const update = usePatchRecurringInvoicesTemplateId({ mutation: { onSuccess: done, onError } })

  const problems = templateProblems(form)
  const pending = create.isPending || update.isPending
  const set = <K extends keyof TemplateForm>(key: K, value: TemplateForm[K]) =>
    setForm((current) => ({ ...current, [key]: value }))

  const save = () => {
    // POST is typed and cannot carry a null; PATCH takes a map and can, which is what
    // lets a cleared constant symbol actually clear.
    if (template?.id !== undefined) {
      update.mutate({ templateId: template.id, data: patchPayload(form) })
      return
    }
    create.mutate({ data: templatePayload(form) })
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={template ? tt('Upraviť šablónu') : tt('Nová šablóna')}
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-card border border-border rounded-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col shadow-2xl"
        onClick={(event) => event.stopPropagation()}
        data-template-editor
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-border shrink-0">
          <h3 className="font-bold text-sm">
            {template ? tt('Upraviť šablónu') : tt('Nová šablóna')}
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label={tt('Zatvoriť')}
            className="p-1.5 rounded-lg hover:bg-muted transition-colors"
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          <Field label={tt('Dodávateľ')}>
            <select
              value={form.supplierId}
              onChange={(event) => set('supplierId', event.target.value)}
              aria-label={tt('Dodávateľ')}
              className={INPUT}
            >
              <option value="">{tt('- Vyberte dodávateľa -')}</option>
              {suppliers.map((supplier) => (
                <option key={supplier.id} value={String(supplier.id)}>
                  {supplier.name}
                </option>
              ))}
            </select>
          </Field>

          <Field label={tt('Názov')}>
            <input
              value={form.name}
              onChange={(event) => set('name', event.target.value)}
              aria-label={tt('Názov šablóny')}
              className={INPUT}
              placeholder={tt('Napr. Mesačný nájom')}
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label={tt('Suma s DPH')}>
              <input
                value={form.amountGross}
                onChange={(event) => set('amountGross', event.target.value)}
                aria-label={tt('Suma s DPH')}
                className={INPUT}
                placeholder="0.00"
              />
            </Field>
            <Field label={tt('DPH %')} hint={tt('0 pre plnenie bez DPH')}>
              <input
                value={form.vatRate}
                onChange={(event) => set('vatRate', event.target.value)}
                aria-label={tt('DPH')}
                className={INPUT}
                placeholder="23"
              />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field
              label={tt('Deň v mesiaci')}
              hint={tt('Najviac 28 - nie každý mesiac má 29. deň')}
            >
              <input
                type="number"
                min="1"
                max={MAX_DAY_OF_MONTH}
                value={form.dayOfMonth}
                onChange={(event) => set('dayOfMonth', event.target.value)}
                aria-label={tt('Deň v mesiaci')}
                className={INPUT}
              />
            </Field>
            <Field label={tt('Splatnosť (dni)')}>
              <input
                type="number"
                min="0"
                value={form.dueDays}
                onChange={(event) => set('dueDays', event.target.value)}
                aria-label={tt('Splatnosť v dňoch')}
                className={INPUT}
              />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label={tt('Konštantný symbol')}>
              <input
                value={form.constantSymbol}
                onChange={(event) => set('constantSymbol', event.target.value)}
                aria-label={tt('Konštantný symbol')}
                className={INPUT}
                placeholder="0308"
              />
            </Field>
            <Field label={tt('Mena')}>
              <input
                value={form.currency}
                onChange={(event) => set('currency', event.target.value)}
                aria-label={tt('Mena')}
                className={INPUT}
                placeholder="EUR"
              />
            </Field>
          </div>

          <Field
            label="IBAN"
            hint={
              form.iban.trim() !== '' && !problems.some((problem) => problem.kind === 'iban-checksum')
                ? formatIban(form.iban)
                : undefined
            }
          >
            <input
              value={form.iban}
              onChange={(event) => set('iban', event.target.value)}
              aria-label="IBAN"
              className={INPUT}
              placeholder="SK00 0000 0000 0000 0000 0000"
            />
          </Field>

          <Field label={tt('Poznámka')}>
            <input
              value={form.description}
              onChange={(event) => set('description', event.target.value)}
              aria-label={tt('Poznámka')}
              className={INPUT}
            />
          </Field>

          {problems.length > 0 && (
            <div className="space-y-1" data-template-problems>
              {problems.map((problem) => (
                <p
                  key={problem.kind}
                  className="flex items-start gap-1.5 text-xs text-red-500 leading-relaxed"
                >
                  <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-px" />
                  <ProblemText problem={problem} />
                </p>
              ))}
            </div>
          )}

          <label className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={form.active}
              onChange={(event) => set('active', event.target.checked)}
              className="w-4 h-4 rounded"
            />
            <span className="text-sm">
              <T>Aktívna - generuje faktúry</T>
            </span>
          </label>
        </div>

        <div className="flex gap-3 px-5 py-4 border-t border-border shrink-0">
          <button
            type="button"
            onClick={onClose}
            className={cn(BUTTON_SECONDARY, 'flex-1 px-4 py-2.5 rounded-xl text-sm')}
          >
            <T>Zrušiť</T>
          </button>
          <button
            type="button"
            onClick={save}
            disabled={pending || !canSave(problems)}
            className={cn('flex-1 px-4 py-2.5 rounded-xl', BUTTON_PRIMARY, 'text-sm')}
          >
            {pending ? <Spinner className="w-4 h-4 mx-auto" /> : <T>Uložiť</T>}
          </button>
        </div>
      </div>
    </div>
  )
}

function ProblemText({ problem }: { problem: TemplateProblem }) {
  if (problem.kind === 'no-supplier') return <T>Vyberte dodávateľa.</T>
  if (problem.kind === 'no-name') return <T>Názov je povinný.</T>
  if (problem.kind === 'no-amount') return <T>Suma musí byť väčšia ako nula.</T>
  if (problem.kind === 'day-out-of-range') {
    return (
      <span>
        <T>Deň v mesiaci musí byť od 1 do 28 - nie každý mesiac má 29. deň.</T>
      </span>
    )
  }
  if (problem.kind === 'due-days-negative') return <T>Splatnosť nemôže byť negatívna.</T>
  return (
    <T>IBAN nemá platnú kontrolnú číslicu. Platba na takýto účet neprejde alebo pôjde niekam inam.</T>
  )
}

const INPUT =
  'w-full bg-background border border-border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/20 transition-all'

function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <div className="space-y-1.5">
      <span className="text-xs text-muted-foreground uppercase tracking-wide font-medium block">
        {label}
      </span>
      {children}
      {hint && <p className="text-[11px] text-muted-foreground/80">{hint}</p>}
    </div>
  )
}


/**
 * The server's own sentence, or null - §9.365, FB-652.
 *
 * **This read `record.error ?? record.message` off the thrown value.** `ApiError` has no `error`
 * property - the envelope is in `data` - and its `message` is the **developer** string built by
 * `buildDeveloperMessage`, so every refusal reached the operator as *"HTTP 400 Bad Request: <the
 * real sentence>"*. `apiErrorMessage`'s own javadoc is the rule it broke: the backend's Slovak
 * message *"with no HTTP status glued to the front and no rewording"*.
 *
 * Thirteen files carried this helper. It delegates now rather than being replaced at each call site,
 * so `?? fallback` still reads the same everywhere and there is one reader instead of thirteen.
 */
function messageOf(error: unknown): string | null {
  return apiErrorField(error, 'error')
}
